
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../config/axiosConfig';

const MemberProfile = () => {
    const location = useLocation();
    // Get slug from the entire path (removing leading and trailing slashes)
    const slug = location.pathname.replace(/^\/|\/$/g, '');
    const [member, setMember] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMember = async () => {
            try {
                // Determine if slug is numeric (likely ID) or string (slug)
                // Backend ViewSet handles both via lookup_value check, but slug is passed as param.
                const response = await axios.get(`nai-members/members/${slug}/`);
                setMember(response.data);
            } catch (err) {
                console.error("Error fetching member:", err);
                setError("Member not found or failed to load.");
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchMember();
        }
    }, [slug]);

    if (loading) return <div className="text-center py-10">Loading profile...</div>;
    if (error) return <div className="text-center py-10 text-red-500">{error}</div>;
    if (!member || Object.keys(member).length === 0) {
        return <div className="text-center py-10 text-gray-500">Member details not available.</div>;
    }

    // Check expiration
    let isExpired = false;
    if (member?.valid_till) {
        const validDate = new Date(member.valid_till);
        const today = new Date();
        validDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        
        if (validDate < today) {
            isExpired = true;
        }
    }

    // QR Code URL - Using GoQR.me or similar public API
    const currentUrl = window.location.href;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(currentUrl)}`;

    // Helper to calculate ages if missing (shared logic, ideally move to utils)
    const getCalculatedAges = (memberData) => {
        let { registered_on, valid_till, member_age, membership_age } = memberData;
        
        if (member_age && membership_age) {
            return { member_age, membership_age };
        }

        const currentYear = new Date().getFullYear();
        let calculatedMembershipAge = membership_age;

        if (registered_on && typeof registered_on === 'string') {
            let regYear = null;
            if (registered_on.includes('-')) {
                 regYear = parseInt(registered_on.split('-')[0]);
            } else if (registered_on.includes('/')) {
                 regYear = parseInt(registered_on.split('/')[2]);
            }
            
            if (regYear) {
                const diff = currentYear - regYear;
                calculatedMembershipAge = diff >= 0 ? `${diff} Years` : '-';
            }
        }
        
        return { 
            membership_age: calculatedMembershipAge || '-' 
        };
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        
        const day = String(date.getDate()).padStart(2, '0');
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        
        return `${day}-${month}-${year}`;
    };

    const { membership_age: displayMembershipAge } = member ? getCalculatedAges(member) : {};

    const isLeader = Boolean(
        member?.member_type?.toUpperCase() === "LEADER" ||
        member?.member_type_choice?.toUpperCase()?.includes("LEADER") ||
        (member?.leadership_designation && member.leadership_designation.trim() !== '')
    );

    const getLeaderLocationRows = (m) => {
        if (!m) return [];
        const place = (m.leadership_place || '').trim().toLowerCase();
        const rows = [];

        const isValid = (val) => {
            if (!val) return false;
            const str = String(val).trim();
            return str !== '' && str.toLowerCase() !== 'not applicable' && str !== '-';
        };

        const isWard = place.includes('ward');
        const isSubDistrict = place.includes('sub');
        const isZone = place.includes('zone');
        const isTaluk = place.includes('taluk');
        const isDistrict = place.includes('district') && !isSubDistrict;
        const isState = place.includes('state') || place.includes('regional');

        let wardVal = isValid(m.ward) ? m.ward : '';
        if (!wardVal && isWard) {
            const match = place.match(/\(([^)]+)\)/);
            if (match) wardVal = match[1].trim();
        }

        // 1. Ward
        if (isWard || wardVal) {
            rows.push({ label: 'Ward', value: wardVal || m.ward || '-' });
        }

        // 2. Sub - District
        if (isSubDistrict || isValid(m.sub_district)) {
            rows.push({ label: 'Sub - District', value: isValid(m.sub_district) ? m.sub_district : '-' });
        }

        // 3. Zone
        if (isZone || isValid(m.zone)) {
            rows.push({ label: 'Zone', value: isValid(m.zone) ? m.zone : '-' });
        }

        // 4. Taluk
        if (isTaluk || isValid(m.taluk)) {
            rows.push({ label: 'Taluk', value: isValid(m.taluk) ? m.taluk : '-' });
        }

        // 5. District (if district leader, or if ward/sub-district/zone/taluk leader with district, or if district is valid)
        if (isDistrict || isValid(m.district) || isWard || isSubDistrict || isZone || isTaluk) {
            if (isDistrict || isValid(m.district)) {
                rows.push({ label: 'District', value: isValid(m.district) ? m.district : '-' });
            }
        }

        // 6. State
        if (isState) {
            rows.push({ label: 'State', value: isValid(m.state) ? m.state : '-' });
        }

        return rows;
    };

    const leaderLocationRows = isLeader ? getLeaderLocationRows(member) : [];

    return (
        <div className="bg-white min-h-screen p-4 md:p-8 font-sans">
            <div className="max-w-3xl mx-auto bg-white shadow-none md:shadow-lg rounded-lg overflow-hidden p-6 border md:border-none">
                
                {/* Header Name */}
                <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 uppercase tracking-wide border-b pb-4">
                    {member?.member_name || "MEMBER PROFILE"}
                </h1>

                <div className="flex flex-col md:flex-row gap-8">
                    
                    {/* Details Section */}
                    <div className="flex-1 space-y-4">
                       
                        {/* Status Label if Expired */}
                        {isExpired && (
                             <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                                <strong className="font-bold">EXPIRED</strong>
                                <span className="block sm:inline"> This membership expired on {formatDate(member?.valid_till)}.</span>
                            </div>
                        )}

                        <table className="w-full text-lg text-gray-700">
                            <tbody>
                                <tr className="border-b border-gray-300">
                                    <td className="font-medium text-gray-900 py-2 whitespace-nowrap align-top">Name</td>
                                    <td className="font-medium text-gray-900 py-2 px-2 align-top">:</td>
                                    <td className="py-2 align-top">{member?.member_name || '-'}</td>
                                </tr>
                                
                                {/* Designation */}
                                <tr className="border-b border-gray-300">
                                    <td className="font-medium text-gray-900 py-2 whitespace-nowrap align-top">
                                        Designation
                                    </td>
                                    <td className="font-medium text-gray-900 py-2 px-2 align-top">:</td>
                                    <td className="py-2 align-top">
                                        {isLeader ? (
                                            <div className="font-bold">
                                                {[member?.leadership_place, member?.leadership_designation].filter(Boolean).join(' ') || '-'}
                                            </div>
                                        ) : (
                                            member?.member_type_choice || member?.member_type || '-'
                                        )}
                                    </td>
                                </tr>

                                {/* Next row(s) below Designation for Leader Location Details (Ward / Sub - District / District etc.) */}
                                {isLeader && leaderLocationRows.map((row, idx) => (
                                    <tr key={idx} className="border-b border-gray-300">
                                        <td className="font-medium text-gray-900 py-2 whitespace-nowrap align-top">{row.label}</td>
                                        <td className="font-medium text-gray-900 py-2 px-2 align-top">:</td>
                                        <td className="py-2 align-top font-semibold">{row.value}</td>
                                    </tr>
                                ))}

                                {/* Show Wings for Leaders */}
                                {isLeader && member?.leadership_wing && (
                                    <tr className="border-b border-gray-300">
                                        <td className="font-medium text-gray-900 py-2 whitespace-nowrap align-top">Wing</td>
                                        <td className="font-medium text-gray-900 py-2 px-2 align-top">:</td>
                                        <td className="py-2 align-top">{member.leadership_wing}</td>
                                    </tr>
                                )}

                                {/* Show Member Type as Leader or Member */}
                                <tr className="border-b border-gray-300">
                                    <td className="font-medium text-gray-900 py-2 whitespace-nowrap align-top">Member Type</td>
                                    <td className="font-medium text-gray-900 py-2 px-2 align-top">:</td>
                                    <td className="py-2 align-top font-semibold">
                                        {isLeader ? "Leader" : "Member"}
                                    </td>
                                </tr>

                                <tr className="border-b border-gray-300">
                                    <td className="font-medium text-gray-900 py-2 whitespace-nowrap align-top">E-News</td>
                                    <td className="font-medium text-gray-900 py-2 px-2 align-top">:</td>
                                    <td className="py-2 align-top font-semibold">Police News Plus.com</td>
                                </tr>

                                {/* Regular Member District */}
                                {!isLeader && (
                                    <tr className="border-b border-gray-300">
                                        <td className="font-medium text-gray-900 py-2 whitespace-nowrap align-top">District</td>
                                        <td className="font-medium text-gray-900 py-2 px-2 align-top">:</td>
                                        <td className="py-2 align-top">{member?.district || '-'}</td>
                                    </tr>
                                )}
                                <tr className="border-b border-gray-300">
                                    <td className="font-medium text-gray-900 py-2 whitespace-nowrap align-top">Registration from</td>
                                    <td className="font-medium text-gray-900 py-2 px-2 align-top">:</td>
                                    <td className="py-2 align-top">{formatDate(member.registered_on)}</td>
                                </tr>
                                <tr className="border-b border-gray-300">
                                    <td className="font-medium text-gray-900 py-2 whitespace-nowrap align-top">Member in years</td>
                                    <td className="font-medium text-gray-900 py-2 px-2 align-top">:</td>
                                    <td className="py-2 align-top">{displayMembershipAge}</td>
                                </tr>
                                <tr className="border-b border-gray-300">
                                    <td className="font-medium text-gray-900 py-2 whitespace-nowrap align-top">Valid upto</td>
                                    <td className="font-medium text-gray-900 py-2 px-2 align-top">:</td>
                                    <td className={`py-2 align-top ${isExpired ? "text-red-600 font-bold" : ""}`}>
                                        {formatDate(member.valid_till)}
                                    </td>
                                </tr>
                                <tr className="border-b border-gray-300">
                                    <td className="font-medium text-gray-900 py-2 whitespace-nowrap align-top">Card Status</td>
                                    <td className="font-medium text-gray-900 py-2 px-2 align-top">:</td>
                                    <td className={`py-2 align-top font-bold ${isExpired ? "text-red-600" : "text-green-600"}`}>
                                        {isExpired ? "EXPIRED" : "ACTIVE"}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                    </div>

                    {/* Image Section */}
                    <div className="w-full md:w-48 flex-shrink-0 flex flex-col items-center gap-4">
                        <div className="w-40 h-48 bg-gray-200 border border-gray-300 overflow-hidden shadow-sm">
                             {member.picture ? (
                                <img src={member.picture} alt={member.member_name} className="w-full h-full object-cover" />
                             ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">No Photo</div>
                             )}
                        </div>
                        
                        {/* QR Code */}
                        <div className="w-32 h-32 bg-white p-1 border">
                            <img src={qrCodeUrl} alt="Member QR Code" className="w-full h-full" />
                        </div>
                    </div>

                </div>

                {/* Footer Section */}
                <div className="mt-8 pt-6 border-t border-gray-200 space-y-3">
                    <div className="text-lg text-gray-800">
                        <span className="font-medium">CHIEF EDITOR :</span> <span className="font-semibold">A. CHARLES</span>
                    </div>
                    
                    <div className="text-lg text-gray-800">
                        <span className="font-medium">CONTACT :</span> <span className="font-semibold">72000 24451 / 72000 24452 / 72000 24453</span>
                    </div>

                    <div className="text-sm text-gray-600 mt-4 text-center border-t pt-4 italic">
                        Recognized as a Member of the Newsmedia Association of India organization during the period of valid authorization, subject to its rules and regulations.
                    </div>
                </div>

            </div>
        </div>
    );
};

export default MemberProfile;
